import queryString from 'query-string';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router';

import { setAccount } from '../store/accountSlice';
import { setDomain } from '../store/domainSlice';
import { getFacebookUser } from '../store/facebookSlice';
import { getGoogleAccount, getGoogleAnalyticsAccount } from '../store/googleSlice';
import { getKlaviyoAccount } from '../store/klaviyoSlice';
import { setSuggestSubscriptionAlert } from '../store/subscriptionSlice';
import { getTiktokAccount } from '../store/tiktokSlice';
import { getWhatsAppAccount } from '../store/whatsappSlice';

import AppNavbar from '../components/AppNavbar';
import AppSidebar from '../components/AppSidebar';
import LucyLauncher from '../components/ChatBot/LucyLauncher';
import Footer from '../components/Footer';

import BrandContextPage from './BrandContextPage';
import DashboardPage from './analytics/dashboards/DashboardPage';
import AttributedPurchasesPage from './analytics/dashboards/AttributedPurchasesPage';
import SettingsCommunicationMessaging from './settings/SettingsCommunicationMessaging';
import SettingsMetaPage from './settings/SettingsMetaPage';
import SettingsPage from './settings/SettingsPage';

import ReportsDetailsPage from './analytics/reports/ReportsDetailsPage';
import ReportsNewPage from './analytics/reports/ReportsNewPage';
import ReportsPage from './analytics/reports/ReportsPage';

import NewPagePage from './pages/NewPagePage';
import PagesPage from './pages/PagesPage';

import RecommenderStatisticsPage from './pages/recommenders/RecommenderStatisticsPage';
import RecommendersDetailsPage from './pages/recommenders/RecommendersDetailsPage';
import RecommendersNewPage from './pages/recommenders/RecommendersNewPage';

import BannerBlockStatisticsPage from './pages/bannerBlocks/BannerBlockStatisticsPage';
import BannerBlocksDetailsPage from './pages/bannerBlocks/BannerBlocksDetailsPage';
import BannerBlocksNewPage from './pages/bannerBlocks/BannerBlocksNewPage';

import ProfilesDetailsPage from './profiles/ProfilesDetailsPage';
import ProfilesEditPage from './profiles/ProfilesEditPage';
import ProfilesNewPage from './profiles/ProfilesNewPage';
import ProfilesPage from './profiles/ProfilesPage';

import EventsPage from './events/EventsPage';

import ProductsDetailsPage from './products/ProductsDetailsPage';
import ProductsPage from './products/ProductsPage';

import CampaignProposalsPage from './campaigns/CampaignProposalsPage';
import CampaignStatisticsPage from './campaigns/CampaignStatisticsPage';
import CampaignsDetailsPage from './campaigns/CampaignsDetailsPage';
import CampaignsNewPage from './campaigns/CampaignsNewPage';
import CampaignsPage from './campaigns/CampaignsPage';

import WorkflowStatisticsPage from './workflows/WorkflowStatisticsPage';
import WorkflowsDetailsPage from './workflows/WorkflowsDetailsPage';
import WorkflowsNewPage from './workflows/WorkflowsNewPage';
import WorkflowsPage from './workflows/WorkflowsPage';

import FlowProposalDetailsPage from './flowProposals/FlowProposalDetailsPage';
import FlowProposalsPage from './flowProposals/FlowProposalsPage';

import PipelinesDetailsPage from './pipelines/PipelinesDetailsPage';
import PipelinesNewPage from './pipelines/PipelinesNewPage';
import PipelinesPage from './pipelines/PipelinesPage';

import TemplatesDetailsPage from './templates/TemplatesDetailsPage';
import TemplatesNewPage from './templates/TemplatesNewPage';
import TemplatesPage from './templates/TemplatesPage';

import PushNotificationsDetailsPage from './push/PushNotificationsDetailsPage';
import PushNotificationsNewPage from './push/PushNotificationsNewPage';
import PushNotificationsPage from './push/PushNotificationsPage';
import PushNotificationsStatisticsPage from './push/PushNotificationsStatisticsPage';

import EmailsDetailsPage from './emails/EmailsDetailsPage';
import EmailsNewPage from './emails/EmailsNewPage';
import EmailsPage from './emails/EmailsPage';
import EmailsStatisticsPage from './emails/EmailsStatisticsPage';

import ProductFeedsDetailsPage from './productFeeds/ProductFeedsDetailsPage';
import ProductFeedsNewPage from './productFeeds/ProductFeedsNewPage';
import ProductFeedsPage from './productFeeds/ProductFeedsPage';
import ProductFeedsStatisticsPage from './productFeeds/ProductFeedsStatisticsPage';

import SurveysDetailsPage from './surveys/SurveysDetailsPage';
import SurveysNewPage from './surveys/SurveysNewPage';
import SurveysPage from './surveys/SurveysPage';
import SurveysStatisticsPage from './surveys/SurveysStatisticsPage';

import NPSAnalyticsPage from './nps/NPSAnalyticsPage';
import NPSSurveyDetailsPage from './nps/NPSSurveyDetailsPage';
import NPSSurveyNewPage from './nps/NPSSurveyNewPage';
import NPSSurveysPage from './nps/NPSSurveysPage';

import TextMessagesDetailsPage from './messages/TextMessagesDetailsPage';
import TextMessagesNewPage from './messages/TextMessagesNewPage';
import TextMessagesPage from './messages/TextMessagesPage';
import TextMessagesStatisticsPage from './messages/TextMessagesStatisticsPage';

import WebhooksDetailsPage from './webhooks/WebhooksDetailsPage';
import WebhooksNewPage from './webhooks/WebhooksNewPage';
import WebhooksPage from './webhooks/WebhooksPage';
import WebhooksStatisticsPage from './webhooks/WebhooksStatisticsPage';

import ViberMessagesDetailsPage from './viber/ViberMessagesDetailsPage';
import ViberMessagesNewPage from './viber/ViberMessagesNewPage';
import ViberMessagesPage from './viber/ViberMessagesPage';
import ViberMessagesStatisticsPage from './viber/ViberMessagesStatisticsPage';

import FacebookProductSetsDetailsPage from './facebook/FacebookProductSetsDetailsPage';
import FacebookProductSetsNewPage from './facebook/FacebookProductSetsNewPage';
import FacebookProductSetsPage from './facebook/FacebookProductSetsPage';

import CouponHistoryPage from './coupons/CouponHistoryPage';
import CouponsDetailsPage from './coupons/CouponsDetailsPage';
import CouponsNewPage from './coupons/CouponsNewPage';
import CouponsPage from './coupons/CouponsPage';

import SegmentsDetailsPage from './segments/SegmentsDetailsPage';
import SegmentsNewPage from './segments/SegmentsNewPage';
import SegmentsPage from './segments/SegmentsPage';

import SyncSegmentsDetailsPage from './syncSegments/SyncSegmentsDetailsPage';
import SyncSegmentsNewPage from './syncSegments/SyncSegmentsNewPage';
import SyncSegmentsPage from './syncSegments/SyncSegmentsPage';

import AssetsDetailsPage from './assets/AssetsDetailsPage';
import AssetsNewPage from './assets/AssetsNewPage';
import AssetsPage from './assets/AssetsPage';

import WhatsappMessagesDetailsPage from './whatsapp/WhatsappMessagesDetailsPage';
import WhatsappMessagesNewPage from './whatsapp/WhatsappMessagesNewPage';
import WhatsappMessagesPage from './whatsapp/WhatsappMessagesPage';
import WhatsappMessagesStatisticsPage from './whatsapp/WhatsappMessagesStatisticsPage';

import GeneratedContentDetailsPage from './generatedContent/GeneratedContentDetailsPage';

import SetupGuidePage from './SetupGuidePage';

import AcceptTermsModal from '../components/Modals/AcceptTermsModal';
import ServiceDisabledAlert from '../components/ServiceDisabledAlert';
import useGuide from '../components/SetupGuide/useGuide';
import ShopifyActivationAlert from '../components/ShopifyActivationAlert';
import Spinner from '../components/Spinner';
import useFetch from '../helpers/useFetch';
import NotFoundPage from './NotFoundPage';
import SpradzInfluencersPage from './SpradzInfluencersPage';
import AbTestsDetailsPage from './analytics/abTests/AbTestsDetailsPage';
import AbTestsNewPage from './analytics/abTests/AbTestsNewPage';
import AbTestsPage from './analytics/abTests/AbTestsPage';
import CustomerInsightsPage from './analytics/customerInsights/CustomerInsightsPage';
import ProductTrendsPage from './analytics/productTrends/ProductTrendsPage';
import CatalogTriggersDetailsPage from './catalogTriggers/CatalogTriggersDetailsPage';
import CatalogTriggersNewPage from './catalogTriggers/CatalogTriggersNewPage';
import CatalogTriggersPage from './catalogTriggers/CatalogTriggersPage';
import FacebookAdsDetailsPage from './facebookAds/FacebookAdsDetailsPage';
import FacebookAdsNewPage from './facebookAds/FacebookAdsNewPage';
import FacebookAdsPage from './facebookAds/FacebookAdsPage';
import ShopifyIntegrationPage from './integration/ShopifyIntegrationPage';
import BannerStatisticsPage from './pages/banners/BannerStatisticsPage';
import BannersDetailsPage from './pages/banners/BannersDetailsPage';
import BannersNewPage from './pages/banners/BannersNewPage';

import { Box } from '@mantine/core';
import SuggestSubscriptionModal from '../components/Subscriptions/SuggestSubscriptionModal';
import SuggestSubscriptionAlert from '../components/SuggestSubscriptionAlert';
import { setSuggestSubscriptionModal } from '../store/subscriptionSlice';
import AgentsPage from './AgentsPage';
import BrandContextConfirmPage from './BrandContextConfirmPage';
import ChatBotPage from './ChatBotPage';
import ConnectorsPage from './ConnectorsPage';
import LoyaltyPage from './LoyaltyPage';
import SearchAsYouTypePage from './SearchAsYouTypePage';
import CompaniesDetailsPage from './companies/CompaniesDetailsPage';
import CompaniesEditPage from './companies/CompaniesEditPage';
import CompaniesNewPage from './companies/CompaniesNewPage';
import CompaniesPage from './companies/CompaniesPage';
import EmailsClickAnalyticsPage from './emails/EmailsClickAnalyticsPage';
import GeneratedContentLandingPage from './generatedContent/GeneratedContentLandingPage';
import GoogleAdsDetailsPage from './googleAds/GoogleAdsDetailsPage';
import GoogleAdsNewPage from './googleAds/GoogleAdsNewPage';
import GoogleAdsPage from './googleAds/GoogleAdsPage';
import BannersPage from './pages/banners/BannersPage';
import StoriesDetailsPage from './stories/StoriesDetailsPage';
import StoriesNewPage from './stories/StoriesNewPage';
import StoriesPage from './stories/StoriesPage';
import StoryStatisticsPage from './stories/StoryStatisticsPage';
import StripoTemplatesPage from './stripoTemplates/StripoTemplatesPage';

const DomainPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { accountId, domainId } = useParams();
  const { user } = useSelector((state) => state.auth);
  const { account } = useSelector((state) => state.account);
  const { domain } = useSelector((state) => state.domain);
  const { suggestSubscriptionModal } = useSelector((state) => state.subscription);

  const guide = useGuide();

  const onPrefixChange = useCallback(
    (domain) => {
      let newSearch = queryString.parse(location.search);
      if (!domain || !user) {
        return;
      }

      const currentUrlPrefix = `/accounts/${accountId}/domains/${domainId}`;
      const newUrlPrefix = `/accounts/${domain.accountId}/domains/${domain.id}`;
      let newPathName = location.pathname.replace(currentUrlPrefix, newUrlPrefix);

      if (newPathName.includes('/recommenders/')) {
        newPathName = `/accounts/${domain.accountId}/domains/${domain.id}/pages`;
        newSearch.type = 'recommenders';
      } else if (newPathName.includes('/bannerBlocks/')) {
        newPathName = `/accounts/${domain.accountId}/domains/${domain.id}/pages`;
        newSearch.type = 'bannerBlocks';
      } else if (newPathName.endsWith('/new')) {
        newPathName = newPathName.split('/').slice(0, -1).join('/');
      } else if (
        newPathName.endsWith('/details') ||
        (newPathName.endsWith('/analytics') &&
          newPathName !== `/accounts/${domain.accountId}/domains/${domain.id}/analytics`)
      ) {
        newPathName = newPathName.split('/').slice(0, -2).join('/');
      } else if (newPathName.includes('/products/')) {
        newPathName = `/accounts/${domain.accountId}/domains/${domain.id}/products`;
      } else if (newPathName.includes('/profiles/') && !newPathName.endsWith('/profiles/')) {
        newPathName = `/accounts/${domain.accountId}/domains/${domain.id}/profiles`;
      } else if (newPathName.includes('/segments/')) {
        newPathName = `/accounts/${domain.accountId}/domains/${domain.id}/segments`;
      } else if (newPathName.includes('/assets/')) {
        newPathName = `/accounts/${domain.accountId}/domains/${domain.id}/assets`;
      }

      delete newSearch.page;

      navigate({
        pathname: newPathName,
        search: queryString.stringify(newSearch),
      });
    },
    [accountId, domainId, location.pathname, location.search, user, navigate]
  );

  const selectDomain = useCallback(
    (domain) => {
      onPrefixChange(domain);
    },
    [onPrefixChange]
  );

  // Clear subscription alert when route changes
  useEffect(() => {
    dispatch(setSuggestSubscriptionAlert(false));
  }, [location.pathname, dispatch]);

  useEffect(() => {
    let account = user.userRoles
      .filter((role) => role.account)
      .map((role) => role.account)
      .find((account) => account.id === parseInt(accountId));

    if (!account) {
      account = user.userRoles.find((r) => r.account)?.account;
      if (!account) {
        navigate('/');
        return;
      } else {
        navigate('/');
      }
    }

    let domain = account.domains.find((domain) => domain.id === parseInt(domainId));
    if (!domain) {
      navigate('/');
      return;
    }

    dispatch(getFacebookUser({ accountId: account?.id, domainId: domain?.id }));
    dispatch(getWhatsAppAccount({ accountId: account?.id, domainId: domain?.id }));
    dispatch(getGoogleAccount({ accountId: account?.id, domainId: domain?.id }));
    dispatch(getGoogleAnalyticsAccount({ accountId: account?.id, domainId: domain?.id }));
    dispatch(getTiktokAccount({ accountId: account?.id, domainId: domain?.id }));
    dispatch(getKlaviyoAccount({ accountId: account?.id, domainId: domain?.id }));

    fetch(`/api/admin/accounts/${account.id}/domains/${domain.id}`, {
      method: 'GET',
    }).then(({ domain: fullDomain }) => {
      dispatch(setAccount(fullDomain.account));
      dispatch(setDomain(fullDomain));

      if (
        !user.email.endsWith('@releva.ai') &&
        (fullDomain.platform === 'merchantpro' ||
          fullDomain.platform === 'shopify' ||
          fullDomain.platform === 'gomag') &&
        !fullDomain.integrationActive
      ) {
        navigate(`/accounts/${account.id}/domains/${domain.id}/activate`);
        return;
      }

      const analyticsListPaths = ['analytics', 'customerInsights'].map(
        (page) => `/accounts/${account.id}/domains/${fullDomain.id}/${page}`
      );
      if (analyticsListPaths.includes(location.pathname) && fullDomain.plan?.restrictions?.includes('analyticsList')) {
        navigate(`/accounts/${account.id}/domains/${fullDomain.id}/generatedLanding`);
        return;
      }
    });
  }, [accountId, domainId, user.userRoles, user.email, dispatch, fetch, navigate, location.pathname, user]);

  if (!domain || !account || !user || account.id !== parseInt(accountId) || domain.id !== parseInt(domainId)) {
    return <Spinner />;
  }

  const shouldAcceptTerms =
    !user.email.endsWith('@releva.ai') && (!domain.termsAcceptedAt || !domain.pricingAcceptedAt);

  return (
    <div className="domain-page-layout">
      <AppNavbar guide={guide} selectDomain={selectDomain} />
      <LucyLauncher />
      <div className="domain-page-body">
        <AppSidebar />
        <Box className="App">
          <AcceptTermsModal isOpen={shouldAcceptTerms} />
          <ShopifyActivationAlert />
          <ServiceDisabledAlert />
          <SuggestSubscriptionAlert />
          {suggestSubscriptionModal && (
            <SuggestSubscriptionModal
              show={suggestSubscriptionModal}
              close={() => dispatch(setSuggestSubscriptionModal(!suggestSubscriptionModal))}
            />
          )}
          <div style={{ flex: 1 }}>
            <Routes>
              <Route path="analytics" element={<DashboardPage />} />
              {/* The drill-down behind the At a Glance tab's attributed-revenue
                    card. A page of its own rather than a modal on the tab: it is
                    paged, it is linkable, and the range it reports has to survive
                    being shared or reloaded. */}
              <Route path="analytics/attributedPurchases" element={<AttributedPurchasesPage />} />
              <Route exact path="reports" element={<ReportsPage />} />
              <Route exact path="loyalty" element={<LoyaltyPage />} />
              <Route exact path="reports/new" element={<ReportsNewPage />} />
              <Route exact path="reports/:reportId/details" element={<ReportsDetailsPage />} />

              <Route exact path="abTests" element={<AbTestsPage />} />
              <Route exact path="abTests/new" element={<AbTestsNewPage />} />
              <Route exact path="abTests/:abTestId/details" element={<AbTestsDetailsPage />} />

              <Route exact path="productTrends" element={<ProductTrendsPage />} />

              <Route exact path="customerInsights" element={<CustomerInsightsPage />} />

              <Route exact path="generatedContent" element={<GeneratedContentDetailsPage />} />
              <Route exact path="generatedContent/:generatedContentId" element={<GeneratedContentDetailsPage />} />
              <Route exact path="generatedLanding" element={<GeneratedContentLandingPage />} />
              <Route exact path="brandContext" element={<BrandContextPage />} />
              <Route exact path="confirm/brandContext" element={<BrandContextConfirmPage />} />

              <Route exact path="pages" element={<PagesPage />} />
              <Route exact path="pages/new" element={<NewPagePage />} />

              <Route exact path="pages/:pageId/recommenders/new" element={<RecommendersNewPage />} />
              <Route exact path="pages/:pageId/recommenders/:id/details" element={<RecommendersDetailsPage />} />
              <Route exact path="pages/:pageId/recommenders/:id/analytics" element={<RecommenderStatisticsPage />} />

              <Route exact path="pages/:pageId/bannerBlocks/new" element={<BannerBlocksNewPage />} />
              <Route exact path="pages/:pageId/bannerBlocks/:id/details" element={<BannerBlocksDetailsPage />} />
              <Route exact path="pages/:pageId/bannerBlocks/:id/analytics" element={<BannerBlockStatisticsPage />} />

              <Route
                exact
                path="pages/:pageId/postPurchases/new"
                element={<BannerBlocksNewPage type="postPurchases" />}
              />
              <Route
                exact
                path="pages/:pageId/postPurchases/:id/details"
                element={<BannerBlocksDetailsPage type="postPurchases" />}
              />
              <Route
                exact
                path="pages/:pageId/postPurchases/:id/analytics"
                element={<BannerBlockStatisticsPage type="postPurchases" />}
              />

              <Route exact path="campaigns" element={<CampaignsPage />} />
              <Route exact path="campaigns/new" element={<CampaignsNewPage />} />
              <Route exact path="campaigns/:campaignId/details" element={<CampaignsDetailsPage />} />
              <Route exact path="campaigns/:campaignId/analytics" element={<CampaignStatisticsPage />} />
              <Route exact path="campaignProposals" element={<CampaignProposalsPage />} />

              <Route exact path="facebook/ads" element={<FacebookAdsPage />} />
              <Route exact path="facebook/ads/new" element={<FacebookAdsNewPage />} />
              <Route exact path="facebook/ads/:facebookAdId/details" element={<FacebookAdsDetailsPage />} />

              <Route exact path="profiles" element={<ProfilesPage />} />
              <Route exact path="profiles/new" element={<ProfilesNewPage />} />
              <Route exact path="profiles/:id/edit" element={<ProfilesEditPage />} />
              <Route path="profiles/:id" element={<ProfilesDetailsPage />} />
              <Route path="profiles/:id/:subpage" element={<ProfilesDetailsPage />} />

              <Route exact path="companies" element={<CompaniesPage />} />
              <Route exact path="companies/new" element={<CompaniesNewPage />} />
              <Route exact path="companies/:id/edit" element={<CompaniesEditPage />} />
              <Route path="companies/:id" element={<CompaniesDetailsPage />} />
              <Route path="companies/:id/:subpage" element={<CompaniesDetailsPage />} />

              <Route exact path="events" element={<EventsPage />} />

              <Route exact path="products" element={<ProductsPage />} />
              <Route exact path="products/:productId" element={<ProductsDetailsPage />} />

              <Route exact path="productFeeds" element={<ProductFeedsPage />} />
              <Route exact path="productFeeds/new" element={<ProductFeedsNewPage />} />
              <Route exact path="productFeeds/:productFeedId/analytics" element={<ProductFeedsStatisticsPage />} />
              <Route exact path="productFeeds/:productFeedId/details" element={<ProductFeedsDetailsPage />} />

              <Route exact path="surveys" element={<SurveysPage />} />
              <Route exact path="surveys/new" element={<SurveysNewPage />} />
              <Route exact path="surveys/:surveyId/analytics" element={<SurveysStatisticsPage />} />
              <Route exact path="surveys/:surveyId/details" element={<SurveysDetailsPage />} />

              <Route exact path="nps" element={<NPSSurveysPage />} />
              <Route exact path="nps/new" element={<NPSSurveyNewPage />} />
              <Route exact path="nps/:npsId/analytics" element={<NPSAnalyticsPage />} />
              <Route exact path="nps/:npsId/details" element={<NPSSurveyDetailsPage />} />

              <Route exact path="workflows" element={<WorkflowsPage />} />
              <Route exact path="workflows/new" element={<WorkflowsNewPage />} />
              <Route exact path="workflows/:campaignId/details" element={<WorkflowsDetailsPage />} />
              <Route exact path="workflows/:campaignId/analytics" element={<WorkflowStatisticsPage />} />

              <Route exact path="flowProposals" element={<FlowProposalsPage />} />
              <Route exact path="flowProposals/:proposalId" element={<FlowProposalDetailsPage />} />

              <Route exact path="pipelines" element={<PipelinesPage />} />
              <Route exact path="pipelines/new" element={<PipelinesNewPage />} />
              <Route exact path="pipelines/:pipelineId/details" element={<PipelinesDetailsPage />} />

              <Route exact path="templates" element={<TemplatesPage />} />
              <Route exact path="templates/new" element={<TemplatesNewPage />} />
              <Route exact path="templates/:templateId/details" element={<TemplatesDetailsPage />} />

              <Route exact path="banners" element={<BannersPage />} />
              <Route exact path="banners/new" element={<BannersNewPage />} />
              <Route exact path="banners/:id/details" element={<BannersDetailsPage />} />
              <Route exact path="banners/:id/analytics" element={<BannerStatisticsPage />} />

              <Route exact path="stories" element={<StoriesPage />} />
              <Route exact path="stories/new" element={<StoriesNewPage />} />
              <Route exact path="stories/:id/details" element={<StoriesDetailsPage />} />
              <Route exact path="stories/:id/analytics" element={<StoryStatisticsPage />} />

              <Route exact path="push" element={<PushNotificationsPage />} />
              <Route exact path="push/new" element={<PushNotificationsNewPage />} />
              <Route exact path="push/:pushNotificationId/details" element={<PushNotificationsDetailsPage />} />
              <Route exact path="push/:pushNotificationId/analytics" element={<PushNotificationsStatisticsPage />} />

              <Route exact path="emails" element={<EmailsPage />} />
              <Route exact path="emails/new" element={<EmailsNewPage />} />
              <Route exact path="emails/:emailId/analytics" element={<EmailsStatisticsPage />} />
              <Route exact path="emails/:emailId/details" element={<EmailsDetailsPage />} />
              <Route exact path="emails/:emailId/clickAnalytics" element={<EmailsClickAnalyticsPage />} />

              <Route exact path="textMessages" element={<TextMessagesPage />} />
              <Route exact path="textMessages/new" element={<TextMessagesNewPage />} />
              <Route exact path="textMessages/:textMessageId/details" element={<TextMessagesDetailsPage />} />
              <Route exact path="textMessages/:textMessageId/analytics" element={<TextMessagesStatisticsPage />} />

              <Route exact path="webhooks" element={<WebhooksPage />} />
              <Route exact path="webhooks/new" element={<WebhooksNewPage />} />
              <Route exact path="webhooks/:webhookId/details" element={<WebhooksDetailsPage />} />
              <Route exact path="webhooks/:webhookId/analytics" element={<WebhooksStatisticsPage />} />

              <Route exact path="viber" element={<ViberMessagesPage />} />
              <Route exact path="viber/new" element={<ViberMessagesNewPage />} />
              <Route exact path="viber/:viberMessageId/details" element={<ViberMessagesDetailsPage />} />
              <Route exact path="viber/:viberMessageId/analytics" element={<ViberMessagesStatisticsPage />} />

              <Route exact path="whatsapp" element={<WhatsappMessagesPage />} />
              <Route exact path="whatsapp/new" element={<WhatsappMessagesNewPage />} />
              <Route exact path="whatsapp/:whatsappMessageId/details" element={<WhatsappMessagesDetailsPage />} />
              <Route exact path="whatsapp/:whatsappMessageId/analytics" element={<WhatsappMessagesStatisticsPage />} />

              {domain.country === 'Indonesia' && <Route exact path="influencers" element={<SpradzInfluencersPage />} />}

              <Route exact path="facebook/productSets" element={<FacebookProductSetsPage />} />
              <Route exact path="facebook/productSets/new" element={<FacebookProductSetsNewPage />} />
              <Route
                exact
                path="facebook/productSets/:facebookProductSetId/details"
                element={<FacebookProductSetsDetailsPage />}
              />

              <Route path="coupons" element={<CouponsPage />} />
              <Route path="coupons/new" element={<CouponsNewPage />} />
              <Route path="coupons/:couponId/details" element={<CouponsDetailsPage />} />
              <Route path="coupons/:couponId/history" element={<CouponHistoryPage />} />

              <Route path="assets" element={<AssetsPage />} />
              <Route path="assets/new" element={<AssetsNewPage />} />
              <Route path="assets/:assetId" element={<AssetsDetailsPage />} />

              <Route path="segments" element={<SegmentsPage />} />
              <Route path="segments/new" element={<SegmentsNewPage />} />
              <Route exact path="segments/:segmentId" element={<SegmentsDetailsPage />} />

              <Route exact path="syncSegments" element={<SyncSegmentsPage />} />
              <Route exact path="syncSegments/new" element={<SyncSegmentsNewPage />} />
              <Route exact path="syncSegments/:segmentSyncId/details" element={<SyncSegmentsDetailsPage />} />

              <Route path="catalogTriggers" element={<CatalogTriggersPage />} />
              <Route path="catalogTriggers/new" element={<CatalogTriggersNewPage />} />
              <Route exact path="catalogTriggers/:catalogTriggerId" element={<CatalogTriggersDetailsPage />} />

              <Route path="settings/*" element={<SettingsPage />} />
              <Route path="settings/messagingAndAds/*" element={<SettingsCommunicationMessaging />} />
              <Route path="settings/meta/*" element={<SettingsMetaPage />} />

              <Route path="shopifyIntegration/" element={<ShopifyIntegrationPage />} />
              <Route path="guide" element={<SetupGuidePage guide={guide} />} />

              <Route path="searchAsYouType" element={<SearchAsYouTypePage />} />

              <Route path="chatBot" element={<ChatBotPage />} />

              <Route path="agents" element={<AgentsPage />} />

              <Route path="connectors" element={<ConnectorsPage />} />

              <Route exact path="stripoTemplates" element={<StripoTemplatesPage />} />

              <Route exact path="google/ads" element={<GoogleAdsPage />} />
              <Route exact path="google/ads/new" element={<GoogleAdsNewPage />} />
              <Route exact path="google/ads/:googleAdId/details" element={<GoogleAdsDetailsPage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
          <Footer />
        </Box>
      </div>
    </div>
  );
};
export default DomainPage;
